import { PropsWithChildren, useCallback } from 'react';
import { Box } from '@mui/material';

import {
  CircularProgress,
  EmptyDataTable,
  LoadingOverlay,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  NO_DATA_PLACEHOLDER_DASH,
} from '@occc/ui-design-system';

import { PromocodesIndividualRowActions } from './promocodes-individual-row-actions.component';

import {
  getPromoCodeGroupStatusLabel,
  getPromoCodeGroupUploadCounts,
} from '../PromocodesConfiguration/promocodes-individual-configuration.utils';
import { PromoCodeGroupFormValues } from '../PromocodesConfiguration/promocodes-configuration.types';
import { useDeletePromoCodeGroup } from '../../hooks';

const formatUploadCount = (value: number | null) =>
  value === null ? NO_DATA_PLACEHOLDER_DASH : value;

const TABLE_STATUS_COLUMN_STYLE = { width: 105 };
const TABLE_COUNT_COLUMN_STYLE = { minWidth: 200, whiteSpace: 'nowrap' };
const TABLE_ACTIONS_COLUMN_STYLE = { width: 30 };

type PromocodesIndividualTableProps = PropsWithChildren<{
  promoCodeGroups: PromoCodeGroupFormValues[];
  onGroupDeleted?: (groupId: string) => void;
  isDownloadEnabled?: boolean;
  isDeleteEnabled?: boolean;
  isRefetching?: boolean;
}>;

export const PromocodesIndividualTable = ({
  promoCodeGroups,
  onGroupDeleted,
  isDownloadEnabled,
  isDeleteEnabled,
  isRefetching,
  children,
}: PromocodesIndividualTableProps) => {
  const { delete: deletePromoCodeGroup, isLoading } = useDeletePromoCodeGroup();

  const handleDelete = useCallback(
    async (groupId: string) => {
      await deletePromoCodeGroup({ id: groupId });
      onGroupDeleted?.(groupId);
    },
    [deletePromoCodeGroup, onGroupDeleted],
  );

  const showTable = promoCodeGroups.length > 0 || Boolean(children);

  if (!showTable) {
    return (
      <Box px={3} pb={3}>
        <EmptyDataTable
          title='No promo code uploaded'
          description=''
          testId='promo-code-groups-empty'
          sx={{ pt: 6, pb: 3 }}
        />
      </Box>
    );
  }

  return (
    <Box position='relative'>
      <TableContainer sx={{ borderRadius: 0, boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box display='flex' alignItems='center' gap={1}>
                  File Name
                  {isRefetching && <CircularProgress size={14} />}
                </Box>
              </TableCell>
              <TableCell sx={TABLE_STATUS_COLUMN_STYLE}>Status</TableCell>
              <TableCell sx={TABLE_COUNT_COLUMN_STYLE}>Codes Successfully Uploaded</TableCell>
              <TableCell sx={TABLE_COUNT_COLUMN_STYLE}>Codes Not Uploaded</TableCell>
              <TableCell padding='button' align='right' sx={TABLE_ACTIONS_COLUMN_STYLE} />
            </TableRow>
          </TableHead>
          <TableBody>
            {children}
            {promoCodeGroups.map((group) => {
              const { codesSuccessfullyUploaded, codesUploadFailed } =
                getPromoCodeGroupUploadCounts(group);

              return (
                <TableRow key={group.id}>
                  <TableCell>{group.fileName}</TableCell>
                  <TableCell>{getPromoCodeGroupStatusLabel(group.processingStatus)}</TableCell>
                  <TableCell sx={TABLE_COUNT_COLUMN_STYLE}>
                    {formatUploadCount(codesSuccessfullyUploaded)}
                  </TableCell>
                  <TableCell sx={TABLE_COUNT_COLUMN_STYLE}>
                    {formatUploadCount(codesUploadFailed)}
                  </TableCell>
                  <TableCell padding='button' align='right' sx={TABLE_ACTIONS_COLUMN_STYLE}>
                    <PromocodesIndividualRowActions
                      group={group}
                      disabled={!isDownloadEnabled}
                      isDeleteEnabled={isDeleteEnabled}
                      onDelete={handleDelete}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading && <LoadingOverlay rounded loaderSize={32} />}
    </Box>
  );
};
